import { logger } from '@/lib/logger';
import React, { useState, useRef, useEffect } from 'react';
import { format } from 'date-fns';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { CreditCard } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  getUserMessageCount,
  createCheckoutSession,
  createCustomerPortalSession,
  testDatabaseAccess,
  incrementUserMessageCount
} from '@/lib/subscription';
import { useAuth } from '@/lib/auth';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { signOut as firebaseSignOut } from 'firebase/auth';
import { firebaseAuth } from '@/lib/firebase';
import { useNavigate, useLocation } from 'react-router-dom';
import { StripeCheckoutDialog } from '@/components/stripe/StripeCheckoutDialog';
import ForceSubscriptionRefresh from '@/components/subscription/ForceSubscriptionRefresh';
import { useSubscriptionWithTier } from '@/hooks/useSubscription';
import { SubscriptionRenewal } from '@/components/subscription/SubscriptionRenewal';

export function SubscriptionSettings() {
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [, setMessageCount] = useState(0);
  const [diagnosticResult, setDiagnosticResult] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [currentTierName, setCurrentTierName] = useState<string | null>(null);
  const { toast } = useToast();
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { user } = useAuth();
  const messageCountCache = new Map<string | undefined, { count: number; timestamp: number }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Use the subscription hook for tier information
  const { 
    subscription, 
    isLoading: subscriptionLoading, 
    isActive, 
    tierName,
    current_period_end,
    stripe_customer_id,
    refreshSubscription
  } = useSubscriptionWithTier();

  // Handle closing payment modal
  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
  };

  // Refresh subscription on mount if coming from payment
  useEffect(() => {
    const isFromPayment = location.state?.fromPayment;
    if (isFromPayment) {
      logger.debug('Settings page: Coming from payment, refreshing subscription...');
      // Small delay to ensure webhook has processed
      setTimeout(() => {
        refreshSubscription();
      }, 1000);
    }
  }, [location.state]);
  
  useEffect(() => {
    async function loadMessageCount() {
      try {
        let count = 0;
        
        try {
          count = await getUserMessageCount(user?.id);
          logger.debug('Message count loaded: ' + count);
        } catch (countError) {
          logger.error('Error loading message count:', countError);
        }
        
        setMessageCount(count);
      } catch (error) {
        logger.error('Error loading message count:', error);
        toast({
          title: 'Error',
          description: 'Failed to load message count information. Please try again later.',
          variant: 'destructive',
        });
      }
    }
    
    // Only load message count, subscription is handled by useSubscription hook
    if (user?.id) {
      loadMessageCount();
    }
    
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, [toast, user?.id]);

  
  // Check for upgrade parameter in URL
  useEffect(() => {
    if (subscriptionLoading) return;
    
    const searchParams = new URLSearchParams(location.search);
    const upgradeParam = searchParams.get('upgrade');
    
    if (upgradeParam && user) {
      // Handle the upgrade request
      if (upgradeParam === 'premium' || upgradeParam === 'unlimited') {
        // Use window.history to remove the parameter without navigation
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('upgrade');
        window.history.replaceState({}, '', newUrl.toString());

        // Trigger the subscription flow for the requested tier AFTER modifying the URL
        initiateSubscription(upgradeParam);
      }
    }
  }, [subscriptionLoading, location.search, user, navigate]);

  // Handle subscribe button click
  const handleSubscribe = () => {
    if (!user) {
      navigate('/login?redirectTo=/pricing');
      return;
    }
    navigate('/pricing');
  };
  
  // Handle manage subscription button click
  const handleManageSubscription = async () => {
    try {
      setIsActionLoading(true);
      
      try {
        const portalUrl = await createCustomerPortalSession();
        
        if (!portalUrl) {
          logger.error('No portal URL returned');
          toast({
            title: "Error",
            description: "Could not open subscription portal. Please try again later.",
            variant: "destructive",
          });
          return;
        }
        
        // Open Stripe portal in new tab
        window.open(portalUrl, '_blank');
      } catch (err: unknown) {
        // Debug logging to understand the error structure
        console.error('Raw error object:', err);
        console.error('Error type:', typeof err);
        console.error('Error constructor:', err instanceof Error ? err.constructor.name : undefined);
        console.error('Error message:', err instanceof Error ? err.message : undefined);
        console.error('Error toString:', String(err));
        
        logger.error('Error creating customer portal session:', err instanceof Error ? err : new Error(String(err)));
        
        // Safely get the error message - be extra careful
        let errorMessage = "Could not open subscription portal. Please try again later or contact support.";
        
        try {
          if (err instanceof Error) {
            errorMessage = err.message;
          } else if (err && typeof err === 'object') {
            const errObj = err as Record<string, unknown>;
            if (typeof errObj.message === 'string') {
              errorMessage = errObj.message;
            } else {
              const stringValue = String(err);
              if (stringValue !== '[object Object]') {
                errorMessage = stringValue;
              }
            }
          } else if (typeof err === 'string') {
            errorMessage = err;
          }
        } catch (extractError) {
          console.error('Error extracting error message:', extractError);
        }
        
        // Show different error messages based on the error
        // Ensure errorMessage is safe to use with string methods
        const safeMessage = String(errorMessage || "");
        
        if (safeMessage.includes('No subscription found')) {
          toast({
            title: "No Subscription Found",
            description: "You don't have an active subscription to manage. Please subscribe to a plan first.",
            variant: "destructive",
            action: <Button 
              onClick={() => navigate('/pricing')}
              variant="outline"
              className="border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              Subscribe
            </Button>,
          });
        } else if (safeMessage.includes('session has expired') || safeMessage.includes('log in again')) {
          toast({
            title: "Session Expired",
            description: "Your session has expired. Please log in again to manage your subscription.",
            variant: "destructive",
            action: <Button 
              onClick={() => {
                // Clear auth state and redirect to login
                firebaseSignOut(firebaseAuth).then(() => {
                  navigate('/login');
                });
              }}
              variant="outline"
              className="border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              Log In
            </Button>,
          });
        } else {
          // Ensure errorMessage is always a valid string for the toast
          const safeErrorMessage = String(errorMessage || "Could not open subscription portal. Please try again later or contact support.");
          
          toast({
            title: "Error",
            description: safeErrorMessage,
            variant: "destructive",
            action: <Button 
              onClick={() => {
                window.location.href = 'mailto:support@jdsimplified.com?subject=Subscription Portal Error';
              }}
              variant="outline"
              className="border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              Contact Support
            </Button>,
          });
        }
      }
    } finally {
      setIsActionLoading(false);
    }
  };
  
  // Check if user is on free tier
  const isFreeTier = () => {
    return !isActive || tierName === 'Free';
  };
  
  // Check if user is on trial
  const isTrial = () => {
    return subscription?.status === 'trialing' || tierName === 'Trial';
  };
  
  // Check if user has a paid subscription (not free, not trial)
  const hasPaidSubscription = () => {
    return isActive && !isFreeTier() && !isTrial();
  };
  
  // Check if user can manage their subscription (has a Stripe customer ID)
  const canManageSubscription = () => {
    return hasPaidSubscription() && !!stripe_customer_id;
  };
  
  // Helper function to format subscription end date
  const formatSubscriptionEndDate = () => {
    if (!current_period_end) {
      return 'N/A';
    }
    
    return format(new Date(current_period_end), 'MMM d, yyyy');
  };

  // Function to increment message count
  const incrementCount = async () => {
    try {
      setIsActionLoading(true);
      const newCount = await incrementUserMessageCount(user?.id);
      
      setMessageCount(newCount);
      toast({
        title: "Count Incremented",
        description: `Your message count is now ${newCount}`,
      });
    } catch (error) {
      logger.error("Error incrementing count:", error);
      toast({
        title: "Error",
        description: "Failed to increment count. See console for details.",
        variant: "destructive",
      });
    } finally {
      setIsActionLoading(false);
    }
  };
  
  // Function to reset the message count to zero
  const resetMessageCount = async () => {
    try {
      setIsActionLoading(true);
      
      // Find the current month's record and update directly to 0
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      
      logger.debug('Resetting message count to 0...');
      
      // First get any records for this user
      const { data: records, error: fetchError } = await supabase
        .from('message_counts')
        .select('id, count')
        .eq('user_id', user?.id)
        .gte('period_start', firstDayOfMonth.toISOString())
        .lt('period_end', new Date(now.getFullYear(), now.getMonth() + 1, 1).toISOString())
        .order('created_at', { ascending: false });
      
      if (fetchError) {
        logger.error('Error fetching message count records:', fetchError);
        toast({
          title: "Error",
          description: "Failed to find message count records",
          variant: "destructive",
        });
        return;
      }
      
      if (records && records.length > 0) {
        logger.debug(`Found existing record with id ${records[0].id} and count ${records[0].count}. Updating to 0.`);
        
        // Update the most recent record to count=0
        const { error: updateError } = await supabase
          .from('message_counts')
          .update({ 
            count: 0,
            updated_at: new Date().toISOString()
          })
          .eq('id', records[0].id);
        
        if (updateError) {
          logger.error('Error resetting count:', updateError);
          toast({
            title: "Error",
            description: "Failed to reset message count",
            variant: "destructive",
          });
        } else {
          // Success - update the local UI and cache
          setMessageCount(0);
          
          // Clear cache in subscription.ts
          if (messageCountCache && typeof messageCountCache.delete === 'function') {
            messageCountCache.delete(user?.id);
          }
          
          // Set a fresh cache entry
          if (messageCountCache && typeof messageCountCache.set === 'function') {
            messageCountCache.set(user?.id!, {
              count: 0,
              timestamp: Date.now()
            });
          }
          
          toast({
            title: "Count Reset",
            description: "Your message count has been reset to 0",
          });
        }
      } else {
        logger.debug('No message count records found, creating a new one with count=0');
        
        // Create a record with count=0
        const monthStart = firstDayOfMonth.toISOString();
        const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999).toISOString();
        
        const { error: insertError } = await supabase
          .from('message_counts')
          .insert({
            user_id: user?.id,
            count: 0,
            period_start: monthStart,
            period_end: monthEnd,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
        
        if (insertError) {
          logger.error('Error creating record with count=0:', insertError);
          
          // Try upsert if the insert failed (might be a unique constraint violation)
          if (insertError.code === '23505') {
            logger.debug('Unique constraint violation. Trying upsert instead.');
            
            const { error: upsertError } = await supabase
              .from('message_counts')
              .upsert({
                user_id: user?.id,
                count: 0, 
                period_start: monthStart,
                period_end: monthEnd,
                updated_at: new Date().toISOString()
              });
            
            if (upsertError) {
              logger.error('Upsert also failed:', upsertError);
              toast({
                title: "Error",
                description: "Failed to reset message count",
                variant: "destructive",
              });
              return;
            }
          } else {
            toast({
              title: "Error",
              description: "Failed to create message count record",
              variant: "destructive",
            });
            return;
          }
        }
        
        // Update UI and cache regardless of insert or upsert
        setMessageCount(0);
        
        // Clear and update cache
        if (messageCountCache && typeof messageCountCache.delete === 'function') {
          messageCountCache.delete(user?.id);
        }
        
        if (messageCountCache && typeof messageCountCache.set === 'function') {
          messageCountCache.set(user?.id!, {
            count: 0,
            timestamp: Date.now()
          });
        }
        
        toast({
          title: "Count Reset",
          description: "Your message count has been reset to 0",
        });
      }
    } catch (error) {
      logger.error("Error resetting count:", error);
      toast({
        title: "Error",
        description: "Failed to reset message count. See console for details.",
        variant: "destructive",
      });
    } finally {
      setIsActionLoading(false);
    }
  };
  
  // Test database access
  const runDatabaseTest = async () => {
    try {
      setIsActionLoading(true);
      const result = await testDatabaseAccess();
      setDiagnosticResult(JSON.stringify(result, null, 2));
      
      toast({
        title: result.success ? "Test Passed" : "Test Failed",
        description: result.message,
        variant: result.success ? "default" : "destructive",
      });
    } catch (error) {
      logger.error("Error running database test:", error);
      setDiagnosticResult(String(error));
      toast({
        title: "Error",
        description: "Failed to run database test. See console for details.",
        variant: "destructive",
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  // Function to initiate subscription
  const initiateSubscription = async (tierName: string) => {
    try {
      setIsActionLoading(true);
      setCurrentTierName(tierName);
      
      logger.debug('Initiating subscription for tier', { tier: tierName });
      
      const redirectUrlOrClientSecret = await createCheckoutSession(user?.id, tierName.toLowerCase());
      logger.debug('Response from createCheckoutSession', { response: redirectUrlOrClientSecret });
      
      if (redirectUrlOrClientSecret) {
        // Check if the result is a URL or a client secret
        if (redirectUrlOrClientSecret.startsWith('/checkout-confirmation')) {
          logger.debug('Received checkout-confirmation URL, redirecting...');
          window.location.href = redirectUrlOrClientSecret;
        } else if (redirectUrlOrClientSecret.startsWith('http')) {
          logger.debug('Received direct URL, redirecting...');
          window.location.href = redirectUrlOrClientSecret;
        } else {
          logger.debug('Received client secret, showing payment modal...');
          setClientSecret(redirectUrlOrClientSecret);
          setShowPaymentModal(true);
        }
      } else {
        logger.error('No response from createCheckoutSession');
        toast({
          title: 'Error',
          description: 'Failed to create checkout session. Please try again later.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      logger.error('Error creating checkout session:', error);
      
      // Show more detailed error to the user
      let errorMessage = 'Failed to create checkout session. Please try again later.';
      if (error instanceof Error) {
        errorMessage = `Error: ${error.message}`;
      }
      
      toast({
        title: 'Checkout Error',
        description: errorMessage,
        variant: 'destructive',
      });
      
      // Log additional diagnostic information
      logger.debug('Diagnostic information:', {
        tier: tierName.toLowerCase(),
        userId: user?.id,
        // Don't log any sensitive information
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  // Render loading state
  if (subscriptionLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner className="w-8 h-8 text-jdblue" />
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-4xl pb-20 md:pb-8">
      {/* Page Title */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Subscription</h1>
        <p className="text-gray-500 dark:text-gray-300">
          {isFreeTier() ? 'Manage your subscription and track your usage' : 
           isTrial() ? 'Your trial period gives you unlimited access.' :
           `Manage your ${tierName?.toLowerCase()} subscription and track usage`}
        </p>
      </div>
      
      {/* Show renewal alert only for paid subscriptions that are expiring or expired */}
      {hasPaidSubscription() && (
        <div className="mb-6">
          <SubscriptionRenewal />
        </div>
      )}
      
      {/* Subscription Section */}
      <Card className="border dark:border-gray-700 shadow-sm dark:bg-gray-800">
        <CardHeader>
          <CardTitle className="text-xl font-semibold text-gray-900 dark:text-white">Current Plan</CardTitle>
          <CardDescription className="text-gray-500 dark:text-gray-300">
            Your subscription details and status:
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <div className="bg-gray-50 dark:bg-gray-700/80 rounded-lg p-6 border border-gray-100 dark:border-gray-600">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg text-gray-900 dark:text-white font-semibold">
                    {isFreeTier() ? 'Free Tier' : isTrial() ? 'Trial Period' : tierName}
                  </span>
                  {hasPaidSubscription() && (
                    <Badge className="bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                      Active
                    </Badge>
                  )}
                </div>
                {!isFreeTier() && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {isTrial() ? 'Trial expires' : 'Renews'}: {formatSubscriptionEndDate()}
                  </p>
                )}
              </div>
            </div>
          </div>
          
          {/* Upgrade/Manage Section */}
          <Separator className="dark:bg-gray-600" />
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {canManageSubscription() ? 'Subscription Management' : 'Upgrade Your Plan'}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {isFreeTier() ? 'Unlock unlimited messages and premium features.' :
               isTrial() ? 'Continue enjoying unlimited access after your trial ends.' :
               canManageSubscription() ? 'View invoices, update payment methods, or cancel your subscription.' :
               'Contact support for subscription assistance.'}
            </p>
            
            {/* Only show manage subscription button for paid users with Stripe customer ID */}
            {canManageSubscription() ? (
              <Button
                onClick={handleManageSubscription}
                disabled={isActionLoading}
                className="w-full sm:w-auto bg-[#F37022] hover:bg-[#E36012] text-white"
              >
                {isActionLoading ? (
                  <LoadingSpinner className="w-4 h-4 mr-2" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-2" />
                )}
                Manage Subscription
              </Button>
            ) : hasPaidSubscription() && !stripe_customer_id ? (
              /* Show message for paid users without Stripe customer */
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <p>Please contact support to manage your subscription.</p>
                <Button
                  onClick={() => window.location.href = 'mailto:support@jdsimplified.com?subject=Subscription Management'}
                  variant="outline"
                  className="mt-2 border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
                >
                  Contact Support
                </Button>
              </div>
            ) : (
              <Button
                onClick={handleSubscribe}
                disabled={isActionLoading}
                className="w-full sm:w-auto bg-[#F37022] hover:bg-[#E36012] text-white"
              >
                {isActionLoading ? (
                  <LoadingSpinner className="w-4 h-4 mr-2" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-2" />
                )}
                {isTrial() ? 'Upgrade from Trial' : 'Upgrade Your Account'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Developer Tools Section */}
      {process.env.NODE_ENV === 'development' && (
        <Card className="border dark:border-gray-700 shadow-sm dark:bg-gray-800">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-gray-900 dark:text-white">Developer Tools</CardTitle>
            <CardDescription className="text-gray-500 dark:text-gray-300">
              Testing tools for developers
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <p className="text-amber-600 bg-amber-50 p-3 rounded-md border border-amber-200 text-sm">
              Warning: These tools are for development and debugging purposes only.
            </p>
            
            <div className="flex flex-col gap-2 mt-4">
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  onClick={incrementCount}
                  variant="outline"
                  size="sm"
                  disabled={isActionLoading}
                  className="flex items-center gap-1"
                >
                  {isActionLoading ? <LoadingSpinner className="w-4 h-4" /> : null}
                  Increment Message Count
                </Button>
                
                <Button
                  onClick={runDatabaseTest}
                  variant="outline"
                  size="sm"
                  disabled={isActionLoading}
                  className="flex items-center gap-1"
                >
                  {isActionLoading ? <LoadingSpinner className="w-4 h-4" /> : null}
                  Test Database Access
                </Button>
                
                <Button
                  onClick={resetMessageCount}
                  variant="outline"
                  size="sm"
                  disabled={isActionLoading}
                  className="flex items-center gap-1"
                >
                  {isActionLoading ? <LoadingSpinner className="w-4 h-4" /> : null}
                  Reset Count
                </Button>
              </div>
            </div>
            
            {diagnosticResult && (
              <pre className="p-3 bg-gray-100 dark:bg-gray-700 rounded text-xs overflow-auto max-h-40">
                {diagnosticResult}
              </pre>
            )}
            
            {/* Add the ForceSubscriptionRefresh component */}
            <div className="mt-6">
              <h3 className="text-base font-medium mb-2 text-gray-900 dark:text-white">Subscription Testing</h3>
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <React.Suspense fallback={<div className="py-4 flex justify-center"><LoadingSpinner className="w-6 h-6" /></div>}>
                  <ForceSubscriptionRefresh />
                </React.Suspense>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      

      {/* Payment modal */}
      {clientSecret && (
        <StripeCheckoutDialog
          open={showPaymentModal}
          onClose={handleClosePaymentModal}
          clientSecret={clientSecret}
          title={`Complete your ${currentTierName} subscription`}
          description="This will give you unlimited access to Ask JDS features"
          onError={(error) => {
            logger.error('Payment error:', error);
            toast({
              title: "Error",
              description: error.message || 'Payment failed',
              variant: "destructive",
            });
          }}
        />
      )}
      
    </div>
  );
} 
